import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="rounded-none hover:bg-slate-50 px-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="font-heading text-2xl font-bold text-slate-900">PayForeman</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="rounded-none border-slate-300 hover:bg-slate-50"
              >
                Log In
              </Button>
              <Button
                onClick={() => navigate('/register')}
                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
              >
                Register
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-slate-600">Last Updated: January 1, 2025</p>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Ardito Advisors LLC ("we," "our," or "us") operates PayForeman, a collections management platform.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you
              use our service at https://payforeman.arditoadvisors.com (the "Service").
            </p>
            <p className="text-slate-700 leading-relaxed">
              By using our Service, you consent to the data practices described in this policy. If you do not
              agree with the terms of this Privacy Policy, please do not access the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-4">2.1 Personal Information</h3>
            <p className="text-slate-700 leading-relaxed mb-3">
              When you register for an account, we collect:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Full name</li>
              <li>Email address</li>
              <li>Company name</li>
              <li>Password (encrypted)</li>
            </ul>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">2.2 Business Data</h3>
            <p className="text-slate-700 leading-relaxed mb-3">
              When you use the Service, you may upload:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Client information (names, company names, contact details)</li>
              <li>Invoice data (amounts, due dates, payment status)</li>
              <li>Payment records</li>
              <li>Notes and communications related to collections</li>
            </ul>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">2.3 Usage Data</h3>
            <p className="text-slate-700 leading-relaxed mb-3">
              We automatically collect certain information when you access the Service:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and features used</li>
              <li>Time and date of access</li>
              <li>Device information</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-700 leading-relaxed mb-3">We use your information to:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Provide, operate, and maintain the Service</li>
              <li>Process your invoices and collections data</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze usage patterns to improve the Service</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share your information in the following circumstances:
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">4.1 Service Providers</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may share your information with third-party service providers who perform services on our behalf,
              including hosting, authentication, analytics, and customer support.
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">4.2 Legal Requirements</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              We may disclose your information if required to do so by law or in response to valid requests by
              public authorities (e.g., court orders or subpoenas).
            </p>

            <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">4.3 Business Transfers</h3>
            <p className="text-slate-700 leading-relaxed">
              If we are involved in a merger, acquisition, or sale of assets, your information may be transferred
              as part of that transaction.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We implement appropriate technical and organizational security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Encryption of data in transit (SSL/TLS)</li>
              <li>Encryption of sensitive data at rest</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure cloud infrastructure</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure.
              While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">6. Data Retention</h2>
            <p className="text-slate-700 leading-relaxed">
              We retain your personal information and business data for as long as your account is active or as
              needed to provide you with the Service. We will retain and use your information to comply with legal
              obligations, resolve disputes, and enforce our agreements. You may request deletion of your account
              and associated data at any time by contacting us at support@arditoadvisors.com.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">7. Your Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              Depending on your location, you may have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Opt-out:</strong> Opt-out of marketing communications</li>
              <li><strong>Restriction:</strong> Request restriction of processing</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at support@arditoadvisors.com.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">8. Cookies and Tracking</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>Maintain your session and keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Understand how you use the Service</li>
              <li>Improve performance and user experience</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              You can control cookies through your browser settings. However, disabling cookies may limit
              your ability to use certain features of the Service.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">9. Third-Party Services</h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service integrates with third-party authentication and infrastructure providers. These third
              parties have their own privacy policies addressing how they use your information. We are not
              responsible for the privacy practices of these third parties.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect
              personal information from children under 18. If you become aware that a child has provided us
              with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">11. International Data Transfers</h2>
            <p className="text-slate-700 leading-relaxed">
              Your information may be transferred to and maintained on servers located outside of your state,
              province, country, or other governmental jurisdiction. We ensure that appropriate safeguards are
              in place to protect your information in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting
              the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review
              this Privacy Policy periodically for any changes. Changes are effective when posted.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">13. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              If you have questions or concerns about this Privacy Policy, please contact us:
            </p>
            <div className="bg-slate-50 p-4 border border-slate-200">
              <p className="text-slate-900 font-semibold mb-2">Ardito Advisors LLC</p>
              <p className="text-slate-700">Email: support@arditoadvisors.com</p>
              <p className="text-slate-700">Website: https://payforeman.arditoadvisors.com</p>
            </div>
          </section>
        </div>

        {/* Back to home */}
        <div className="mt-12 pt-8 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="rounded-none border-slate-300 hover:bg-slate-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-slate-50 border-t border-slate-200 mt-16">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-600">
          <p>© 2025 Ardito Advisors LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
